import type { FeatureLike } from 'ol/Feature';
import MVT from 'ol/format/MVT';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import { Fill, Stroke, Style, Text } from 'ol/style';
import { defaultHybridReferenceVisibility, type HybridReferenceVisibility } from '../core/types';

export const hybridReferenceUrl = 'https://tiles.openfreemap.org/planet/latest/{z}/{x}/{y}.pbf';

export const hybridReferenceSourceLayers = [
  'transportation',
  'transportation_name',
  'place',
  'water',
  'water_name',
  'waterway',
] as const;

const attribution = '<a href="https://openfreemap.org">OpenFreeMap</a> · <a href="https://www.openmaptiles.org/">© OpenMapTiles</a> · Data from <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const maxWebMercatorResolution = 156543.03392804097;

type RoadClass = 'motorway' | 'trunk' | 'primary' | 'secondary' | 'tertiary' | 'minor' | 'service' | 'track' | 'path';

const roadMinZoom: Record<RoadClass, number> = {
  motorway: 4,
  trunk: 4,
  primary: 6,
  secondary: 8,
  tertiary: 10,
  minor: 12,
  service: 13,
  track: 13,
  path: 14,
};

const roadWidths: Record<RoadClass, readonly [number, number]> = {
  motorway: [4.6, 2.8],
  trunk: [4.3, 2.6],
  primary: [3.9, 2.2],
  secondary: [3.3, 1.8],
  tertiary: [2.8, 1.5],
  minor: [2.3, 1.2],
  service: [1.8, 0.9],
  track: [1.7, 0.9],
  path: [1.4, 0.7],
};

const placeSettings: Partial<Record<string, readonly [number, string]>> = {
  city: [6, '600 13px system-ui, sans-serif'],
  town: [8, '600 12px system-ui, sans-serif'],
  village: [10, '600 11px system-ui, sans-serif'],
  hamlet: [12, '500 10px system-ui, sans-serif'],
  isolated_dwelling: [13, '500 10px system-ui, sans-serif'],
  suburb: [13, '500 10px system-ui, sans-serif'],
  quarter: [14, '500 10px system-ui, sans-serif'],
  neighbourhood: [14, '500 10px system-ui, sans-serif'],
};

const roadStyles = new Map<RoadClass, Style[]>();
const labelStyles = new Map<string, Style>();

const zoomForResolution = (resolution: number): number => Math.log2(maxWebMercatorResolution / resolution);

const asRoadClass = (value: unknown): RoadClass | null => (
  typeof value === 'string' && Object.hasOwn(roadMinZoom, value) ? value as RoadClass : null
);

const roadStyle = (roadClass: RoadClass): Style[] => {
  const cached = roadStyles.get(roadClass);
  if (cached) return cached;
  const [casingWidth, innerWidth] = roadWidths[roadClass];
  const local = roadClass === 'service' || roadClass === 'track' || roadClass === 'path';
  const styles = [
    new Style({ stroke: new Stroke({ color: local ? 'rgba(25,31,35,.72)' : 'rgba(33,38,41,.88)', width: casingWidth }) }),
    new Style({ stroke: new Stroke({
      color: roadClass === 'motorway' || roadClass === 'trunk' ? 'rgba(255,206,105,.96)' : 'rgba(255,255,255,.92)',
      width: innerWidth,
      lineDash: roadClass === 'track' || roadClass === 'path' ? [5, 4] : undefined,
    }) }),
  ];
  roadStyles.set(roadClass, styles);
  return styles;
};

const textStyle = (key: string, text: string, options: {
  readonly color: string;
  readonly font: string;
  readonly placement?: 'point' | 'line';
  readonly strokeColor?: string;
  readonly strokeWidth?: number;
}): Style => {
  const cacheKey = `${key}:${text}`;
  const cached = labelStyles.get(cacheKey);
  if (cached) return cached;
  const style = new Style({
    text: new Text({
      text,
      font: options.font,
      placement: options.placement ?? 'point',
      overflow: false,
      fill: new Fill({ color: options.color }),
      stroke: new Stroke({ color: options.strokeColor ?? 'rgba(15,20,23,.9)', width: options.strokeWidth ?? 3 }),
    }),
  });
  labelStyles.set(cacheKey, style);
  return style;
};

const placeStyle = (feature: FeatureLike, zoom: number): Style | undefined => {
  const placeClass = feature.get('class');
  const name = feature.get('name');
  if (typeof placeClass !== 'string' || typeof name !== 'string' || !name) return undefined;

  const setting = placeSettings[placeClass];
  if (!setting || zoom < setting[0]) return undefined;
  return textStyle(`place:${placeClass}`, name, { color: '#fff', font: setting[1], strokeWidth: 3.5 });
};

const transportationNameStyle = (feature: FeatureLike, zoom: number): Style | undefined => {
  const roadClass = asRoadClass(feature.get('class'));
  if (!roadClass || zoom < Math.max(9, roadMinZoom[roadClass] + 1)) return undefined;
  const name = feature.get('name');
  const ref = feature.get('ref');
  const label = typeof name === 'string' && name ? name : typeof ref === 'string' ? ref : '';
  if (!label) return undefined;
  return textStyle(`road:${roadClass}`, label, {
    color: '#fff',
    font: roadClass === 'motorway' || roadClass === 'trunk' ? '600 10px system-ui, sans-serif' : '500 9px system-ui, sans-serif',
    placement: 'line',
  });
};

const waterNameStyle = (feature: FeatureLike, zoom: number): Style | undefined => {
  const name = feature.get('name');
  if (typeof name !== 'string' || !name || zoom < 7) return undefined;
  const geometryType = feature.getGeometry()?.getType();
  return textStyle('water', name, {
    color: '#aee9ff',
    font: 'italic 500 11px system-ui, sans-serif',
    placement: geometryType === 'LineString' || geometryType === 'MultiLineString' ? 'line' : 'point',
    strokeColor: 'rgba(9,35,48,.92)',
    strokeWidth: 3,
  });
};

const waterAreaStyle = new Style({
  fill: new Fill({ color: 'rgba(45,164,210,.12)' }),
});

const waterwayStyles = new Map<string, Style>();
const waterwayStyle = (feature: FeatureLike, zoom: number): Style | undefined => {
  const waterClass = feature.get('class');
  if (typeof waterClass !== 'string') return undefined;
  const minZoom = waterClass === 'river' ? 7 : waterClass === 'canal' ? 11 : 13;
  if (zoom < minZoom) return undefined;
  const intermittent = feature.get('intermittent') === 1;
  const key = `${waterClass}:${intermittent}`;
  let style = waterwayStyles.get(key);
  if (!style) {
    style = new Style({ stroke: new Stroke({
      color: waterClass === 'river' ? 'rgba(93,211,255,.9)' : 'rgba(111,217,255,.78)',
      width: waterClass === 'river' ? 1.8 : waterClass === 'canal' ? 1.35 : 1,
      lineDash: intermittent ? [5, 4] : undefined,
    }) });
    waterwayStyles.set(key, style);
  }
  return style;
};

export const hybridReferenceStyle = (
  feature: FeatureLike,
  resolution: number,
  visibility: HybridReferenceVisibility = defaultHybridReferenceVisibility,
): Style | Style[] | undefined => {
  const sourceLayer = feature.get('layer');
  const zoom = zoomForResolution(resolution);
  if (!visibility.transportation && (sourceLayer === 'transportation' || sourceLayer === 'transportation_name')) return undefined;
  if (!visibility.places && sourceLayer === 'place') return undefined;
  if (!visibility.hydrography && (sourceLayer === 'water' || sourceLayer === 'water_name' || sourceLayer === 'waterway')) return undefined;
  if (sourceLayer === 'transportation') {
    const roadClass = asRoadClass(feature.get('class'));
    return roadClass && zoom >= roadMinZoom[roadClass] ? roadStyle(roadClass) : undefined;
  }
  if (sourceLayer === 'transportation_name') return transportationNameStyle(feature, zoom);
  if (sourceLayer === 'place') return placeStyle(feature, zoom);
  if (sourceLayer === 'water') return waterAreaStyle;
  if (sourceLayer === 'water_name') return waterNameStyle(feature, zoom);
  if (sourceLayer === 'waterway') return waterwayStyle(feature, zoom);
  return undefined;
};

export const createHybridReferenceLayer = (): VectorTileLayer => new VectorTileLayer({
  properties: {
    basemapRole: 'operational-reference',
    provider: 'OpenFreeMap',
    sourceLayers: [...hybridReferenceSourceLayers],
  },
  declutter: true,
  renderBuffer: 160,
  source: new VectorTileSource({
    attributions: attribution,
    format: new MVT({ layers: [...hybridReferenceSourceLayers] }),
    maxZoom: 14,
    url: hybridReferenceUrl,
  }),
  style: hybridReferenceStyle,
});

export const applyHybridReferenceVisibility = (layer: VectorTileLayer, visibility: HybridReferenceVisibility): void => {
  layer.set('hybridReference', { ...visibility });
  layer.setStyle((feature, resolution) => hybridReferenceStyle(feature, resolution, visibility));
};
